import React, { useEffect, useState } from 'react';
import {
  Bot,
  Loader2,
  DollarSign,
  TrendingUp,
  AlertTriangle,
  Zap,
  RefreshCw,
  Database,
} from 'lucide-react';
import { adminDb } from '../lib/adminDb';
import { adminFetch } from '../lib/api';

interface AiStats {
  totalConversations: number;
  totalMessages: number;
  avgMessagesPerSession: number;
  activeToday: number;
  tokenUsage: {
    total_input: number;
    total_output: number;
    total_cost: number;
    by_type: Record<string, { input: number; output: number; cost: number; count: number }>;
  };
}

interface CacheStats {
  size: number;
  hits: number;
  misses: number;
  sets: number;
  evictions: number;
  hitRate: number;
}

export const AiDashboardPage: React.FC = () => {
  const [stats, setStats] = useState<AiStats>({
    totalConversations: 0,
    totalMessages: 0,
    avgMessagesPerSession: 0,
    activeToday: 0,
    tokenUsage: { total_input: 0, total_output: 0, total_cost: 0, by_type: {} },
  });
  const [cacheStats, setCacheStats] = useState<CacheStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [featureEnabled, setFeatureEnabled] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    setLoading(true);
    try {
      const { data: flagData } = await adminDb
        .from('feature_flags')
        .select('enabled')
        .eq('key', 'ai_coach')
        .single();
      setFeatureEnabled(flagData?.enabled ?? true);

      const { data: users } = await adminDb.from('users').select('data');
      const { data: aiLogs } = await adminDb
        .from('ai_usage_logs')
        .select('*')
        .order('created_at', { ascending: false });

      try {
        const resp = await adminFetch('/api/admin/stats');
        if (resp.ok) {
          const body = await resp.json();
          if (body?.ai?.cache) setCacheStats(body.ai.cache);
        }
      } catch {
        /* cache stats are best-effort */
      }

      let totalConvos = 0,
        totalMsgs = 0,
        todayActive = 0;
      const today = new Date().toDateString();

      (users || []).forEach((u) => {
        const chatHistory = u.data?.chatHistory;
        if (chatHistory && Array.isArray(chatHistory) && chatHistory.length > 0) {
          totalConvos++;
          totalMsgs += chatHistory.length;
          const lastMsg = chatHistory[chatHistory.length - 1];
          if (lastMsg?.timestamp && new Date(lastMsg.timestamp).toDateString() === today)
            todayActive++;
        }
      });

      // Token usage from ai_usage_logs
      const byType: Record<string, { input: number; output: number; cost: number; count: number }> =
        {};
      let totalInput = 0,
        totalOutput = 0,
        totalCost = 0;
      (aiLogs || []).forEach((log) => {
        totalInput += log.input_tokens || 0;
        totalOutput += log.output_tokens || 0;
        totalCost += parseFloat(log.cost_estimate || '0');
        if (!byType[log.prompt_type])
          byType[log.prompt_type] = { input: 0, output: 0, cost: 0, count: 0 };
        byType[log.prompt_type].input += log.input_tokens || 0;
        byType[log.prompt_type].output += log.output_tokens || 0;
        byType[log.prompt_type].cost += parseFloat(log.cost_estimate || '0');
        byType[log.prompt_type].count++;
      });

      setStats({
        totalConversations: totalConvos,
        totalMessages: totalMsgs,
        avgMessagesPerSession: totalConvos > 0 ? Math.round(totalMsgs / totalConvos) : 0,
        activeToday: todayActive,
        tokenUsage: {
          total_input: totalInput,
          total_output: totalOutput,
          total_cost: totalCost,
          by_type: byType,
        },
      });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div>
        <div className="page-header">
          <h1>
            <Bot size={24} /> KI Dashboard
          </h1>
        </div>
        <div style={{ textAlign: 'center', padding: 60 }}>
          <Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} />
        </div>
      </div>
    );
  }

  const typeLabels: Record<string, string> = {
    chat: 'Chat',
    explanation: 'Erklaerung',
    image: 'Bildgenerierung',
    motivation: 'Motivation',
  };

  return (
    <div>
      <div className="page-header">
        <h1>
          <Bot size={24} /> KI Dashboard
        </h1>
        <div className="flex gap-2 items-center">
          <span className={`badge ${featureEnabled ? 'badge-success' : 'badge-danger'}`}>
            {featureEnabled ? 'KI-Coach aktiv' : 'KI-Coach deaktiviert'}
          </span>
          <button className="btn btn-secondary btn-sm" onClick={loadStats}>
            <RefreshCw size={14} />
          </button>
        </div>
      </div>

      {!featureEnabled && (
        <div className="alert alert-danger mb-6">
          <AlertTriangle size={16} />
          <span>KI-Coach ist deaktiviert. Aktiviere ihn unter Feature Flags.</span>
        </div>
      )}

      {/* Usage KPIs */}
      <div className="grid-cols-4 mb-6">
        <div className="kpi-card">
          <span className="kpi-label">Nutzer mit KI-Chat</span>
          <span className="kpi-value">{stats.totalConversations}</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Nachrichten gesamt</span>
          <span className="kpi-value">{stats.totalMessages}</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Ø Nachrichten/Nutzer</span>
          <span className="kpi-value">{stats.avgMessagesPerSession}</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Heute aktiv</span>
          <span className="kpi-value">{stats.activeToday}</span>
        </div>
      </div>

      {/* Token Usage */}
      <div className="grid-cols-3 mb-6">
        <div className="kpi-card">
          <span className="kpi-label">Input Tokens</span>
          <span className="kpi-value">{stats.tokenUsage.total_input.toLocaleString('de-DE')}</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Output Tokens</span>
          <span className="kpi-value">{stats.tokenUsage.total_output.toLocaleString('de-DE')}</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Gesch. Kosten</span>
          <span className="kpi-value" style={{ color: 'var(--success)' }}>
            $
            {stats.tokenUsage.total_cost > 0
              ? stats.tokenUsage.total_cost.toFixed(4)
              : (stats.totalMessages * 0.002).toFixed(2)}
          </span>
        </div>
      </div>

      {/* Token by Type */}
      {Object.keys(stats.tokenUsage.by_type).length > 0 && (
        <div className="card mb-6">
          <h3 style={{ marginBottom: 16 }}>
            <TrendingUp size={18} /> Token-Verbrauch nach Typ
          </h3>
          <table>
            <thead>
              <tr>
                <th>Typ</th>
                <th>Anfragen</th>
                <th>Input Tokens</th>
                <th>Output Tokens</th>
                <th>Kosten</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(stats.tokenUsage.by_type).map(([type, data]) => (
                <tr key={type}>
                  <td>
                    <span className="badge badge-info">{typeLabels[type] || type}</span>
                  </td>
                  <td className="text-sm">{data.count}</td>
                  <td className="text-mono text-sm">{data.input.toLocaleString('de-DE')}</td>
                  <td className="text-mono text-sm">{data.output.toLocaleString('de-DE')}</td>
                  <td className="text-mono text-sm">${data.cost.toFixed(4)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Model Info */}
      <div className="grid-cols-2 mb-6">
        <div className="card">
          <h3 style={{ marginBottom: 12 }}>
            <Zap size={18} /> Modell-Konfiguration
          </h3>
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted">Provider</span>
              <span className="badge badge-info">Google Gemini</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted">Modell</span>
              <span className="text-mono text-sm">gemini-2.5-pro</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted">Max Tokens</span>
              <span className="text-sm">8.192</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted">Temperatur</span>
              <span className="text-sm">0.7</span>
            </div>
          </div>
        </div>
        <div className="card">
          <h3 style={{ marginBottom: 12 }}>
            <DollarSign size={18} /> Vertex AI Listenpreise (USD/1M)
          </h3>
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted">Flash – Input / Output</span>
              <span className="text-mono text-sm">$0.30 / $2.50</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted">Pro – Input / Output</span>
              <span className="text-mono text-sm">$1.25 / $10.00</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted">Flash-Image – Output</span>
              <span className="text-mono text-sm">$30.00</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted">Nachrichten gesamt</span>
              <span className="text-sm">{stats.totalMessages}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Coach Cache Stats */}
      {cacheStats && (
        <div className="card mb-6">
          <h3 style={{ marginBottom: 12 }}>Coach Response Cache (pro Instanz)</h3>
          <div className="grid-cols-4">
            <div className="kpi-card">
              <span className="kpi-label">Hit-Rate</span>
              <span
                className="kpi-value"
                style={{ color: cacheStats.hitRate > 0.2 ? 'var(--success)' : 'var(--warning)' }}
              >
                {(cacheStats.hitRate * 100).toFixed(1)}%
              </span>
            </div>
            <div className="kpi-card">
              <span className="kpi-label">Hits / Misses</span>
              <span className="kpi-value text-mono text-sm">
                {cacheStats.hits.toLocaleString('de-DE')} /{' '}
                {cacheStats.misses.toLocaleString('de-DE')}
              </span>
            </div>
            <div className="kpi-card">
              <span className="kpi-label">Aktive Einträge</span>
              <span className="kpi-value">{cacheStats.size.toLocaleString('de-DE')}</span>
            </div>
            <div className="kpi-card">
              <span className="kpi-label">Evictions</span>
              <span className="kpi-value">{cacheStats.evictions.toLocaleString('de-DE')}</span>
            </div>
          </div>
        </div>
      )}

      {/* Optimization Tips */}
      <div className="card">
        <h3 style={{ marginBottom: 12 }}>Optimierungstipps</h3>
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2 text-sm">
            <span className="badge badge-success">✓</span> Gemini Flash ist kostenoptimal für Chat
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className="badge badge-success">✓</span> Coach-Cache aktiv (24h TTL, 5k Einträge)
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className="badge badge-info">i</span> Prompt-Optimierung kann Tokens um 30%
            reduzieren
          </div>
        </div>
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};
